"use client";

import { useEffect, useRef, useState } from "react";

import FullView from "@/components/common/FullView";
import SubmitForm from "@/components/request/renewal/SubmitForm";
import { useAbExperiment } from "@/hooks/useAbExperiment";
import { trackEvent } from "@/utils/analytics";
import { cn } from "@/utils/cn";

import { HOME_TRIAL_FLOW_EXPERIMENT } from "./homeTrialExperiment";
import TrialHeroVariant from "./TrialHeroVariant";

// 섹션 컨테이너
const sectionClasses = cn(
  "relative flex h-auto w-full max-w-[1200px] flex-col items-center",
  "desktop:h-full desktop:flex-row desktop:items-center desktop:justify-between",
  "desktop:gap-12",
);

// 왼쪽 텍스트 + 폼 컨테이너
const leftContainerClasses = cn(
  "z-10 flex flex-col gap-8",
  "items-center px-side",
  "desktop:max-w-[550px] desktop:flex-shrink-0",
  "desktop:items-start desktop:px-0",
);

// 메인 타이틀
const titleClasses = cn("text-center text-36 font-bold", "leading-[1.3] tracking-tight", "desktop:text-left desktop:text-44");

// 서브 텍스트
const subtextClasses = cn(
  "text-14 text-center",
  "leading-[1.45] tracking-tight text-black/55",
  "tablet:text-18",
  "desktop:text-24 desktop:text-left",
);

// 오른쪽 이미지 외부 컨테이너
const mediaOuterBaseClasses = cn(
  "relative w-full overflow-hidden",
  "mt-12 [@media(max-height:760px)]:mt-4",
  "desktop:mt-0 desktop:flex-1 desktop:flex desktop:justify-end",
);

const videoOuterClasses = cn(mediaOuterBaseClasses, "bg-white leading-none desktop:max-w-[720px]");

const heroVideoClasses = cn("mx-auto block h-auto w-full border-0 outline-none");

const heroVideo = {
  desktop: {
    asset: "ConceptB-Subtitled-Numbered-Desktop",
    width: 1352,
    height: 1208,
  },
  mobile: {
    asset: "ConceptB-Subtitled-Numbered-Mobile",
    width: 1080,
    height: 1920,
  },
  posterExtension: "png",
  version: "20260506-hero-video-final",
} as const;

function HeroControl({ experimentParams }: { experimentParams: { experiment_id: string; variant: string } }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackingFiredRef = useRef(false);
  const [usesMobileVideo, setUsesMobileVideo] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const syncUsesMobileVideo = () => setUsesMobileVideo(mediaQuery.matches);

    syncUsesMobileVideo();
    mediaQuery.addEventListener("change", syncUsesMobileVideo);

    return () => mediaQuery.removeEventListener("change", syncUsesMobileVideo);
  }, []);

  const selectedVideo = usesMobileVideo ? heroVideo.mobile : heroVideo.desktop;

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.muted = true;
    videoRef.current.defaultMuted = true;
    void videoRef.current.play().catch(() => {
      // Keep the poster visible if autoplay is blocked.
    });
  }, [selectedVideo.asset]);

  useEffect(() => {
    if (trackingFiredRef.current) return;
    trackingFiredRef.current = true;
    const trackingContext = {
      ...experimentParams,
      entry_source: "home_hero_ab",
      entry_source_detail: "home_hero_control",
      landing_path: "/",
    };
    trackEvent("hero_control_viewed", "/", trackingContext);
  }, [experimentParams.experiment_id, experimentParams.variant]);

  return (
    <FullView
      as="section"
      className="h-auto min-h-0 w-full justify-start py-10 desktop:h-[calc(100vh-4rem)] desktop:min-h-[calc(100vh-4rem)] desktop:justify-center desktop:py-0"
    >
      <div className={sectionClasses}>
        {/* 왼쪽 텍스트 + 간이 문의 폼 */}
        <div className={leftContainerClasses}>
          {/* 메인 타이틀 */}
          <div className={titleClasses}>
            <h2 className="mb-0">채용 공고로</h2>
            <h2 className="mb-0">만날 수 없는 핵심 인재</h2>
            <h2>1주 안에 커피챗까지</h2>
          </div>

          {/* 서브 텍스트 */}
          <div className={subtextClasses}>
            <p className="mb-0">아웃바운드 채용을 자동화하는 AI 리크루터</p>
            <p>
              <span className="text-primary">✓</span> 업계 평균 대비{" "}
              <span className="font-bold text-primary">8배 채용 성사율</span>로 증명
            </p>
          </div>

          {/* 간이 문의 폼 (성함·이메일만) */}
          <div id="home-inquiry-form" className="w-full desktop:max-w-[440px]">
            <SubmitForm
              compact
              submitText="24시간 내 맞춤 채용 진단 받기"
              eventParams={experimentParams}
              onSubmitAttempted={(valid) => {
                trackEvent("contact_clicked", "/", { ...experimentParams, cta_type: "request", valid });
              }}
            />
          </div>
        </div>

        {/* 오른쪽 영상 영역 */}
        <div className={videoOuterClasses}>
          <video
            key={selectedVideo.asset}
            ref={videoRef}
            className={heroVideoClasses}
            width={selectedVideo.width}
            height={selectedVideo.height}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={`/image/home/${selectedVideo.asset}.${heroVideo.posterExtension}?v=${heroVideo.version}`}
            aria-label="AI 리크루터 플랫폼 소개 영상 preview"
            onCanPlay={(event) => {
              event.currentTarget.muted = true;
              event.currentTarget.defaultMuted = true;
              void event.currentTarget.play().catch(() => {
                // Keep the poster or first frame visible if autoplay is blocked.
              });
            }}
          >
            <source src={`/image/home/${selectedVideo.asset}.mp4?v=${heroVideo.version}`} type="video/mp4" />
          </video>
        </div>
      </div>
    </FullView>
  );
}

function HeroSection() {
  const { variant, isReady } = useAbExperiment(HOME_TRIAL_FLOW_EXPERIMENT, { trackingMode: "page_view" });
  const experimentParams = { experiment_id: HOME_TRIAL_FLOW_EXPERIMENT.id, variant };

  if (!isReady) {
    return (
      <FullView
        as="section"
        className="h-auto min-h-[620px] w-full justify-start py-10 desktop:h-[calc(100vh-4rem)] desktop:min-h-[calc(100vh-4rem)] desktop:justify-center desktop:py-0"
      />
    );
  }

  return variant === "b" ? (
    <TrialHeroVariant experimentParams={experimentParams} />
  ) : (
    <HeroControl experimentParams={experimentParams} />
  );
}

export default HeroSection;
